import React, { forwardRef, PropsWithChildren } from 'react'
import clsx from 'clsx'
import { Link as LinkStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { CommonProps, LinkTarget } from '../helperTypes'

type Props = PropsWithChildren<{
  /** Disabled state */
  disabled?: boolean
  /** url to traverse to */
  href?: string
  /** mouse click handler */
  onClick?: React.MouseEventHandler<HTMLAnchorElement>
  /**
   * The size of the link
   * @default medium
   */
  size?: 'xsmall' | 'small' | 'medium' | 'large'
  /**
   * link target
   * @default _self
   */
  target?: LinkTarget
  /** the title for the link */
  title?: string
  /**
   * Variants of the link
   * @default default
   */
  variant?: 'default' | 'underlined'
}>

type DeprecatedProps = {
  as?: 'a' | 'button'
  handleClick?: React.MouseEventHandler<HTMLAnchorElement>
  inline?: boolean
  label?: string
  primary?: boolean
}

export type LinkProps = Props & DeprecatedProps & CommonProps

const useStyles = createUseStyles(LinkStyles, 'link')

export const Link = (props: LinkProps, ref: React.Ref<HTMLAnchorElement>) => {
  const {
    as,
    children,
    className,
    disabled,
    handleClick,
    inline,
    label,
    onClick,
    primary,
    variant,
    size,
    target,
    title,
    ...rest
  } = props

  const classes = useStyles()
  useDeprecatedProps(
    'Link',
    {
      as: undefined,
      label: 'title',
      primary: 'variant',
      handleClick: 'onClick',
    },
    props,
  )

  return (
    <a
      ref={ref}
      className={clsx(
        className,
        classes.root,
        inline && classes._inline,
        variant === 'underlined' && classes._underlined,
        size && classes[`_${size}`],
        disabled && classes._disabled,
      )}
      onClick={onClick || handleClick}
      aria-disabled={disabled}
      aria-label={label || title}
      target={target}
      title={label || title}
      {...rest}
    >
      {children}
    </a>
  )
}

export default forwardRef<HTMLAnchorElement, LinkProps>(Link)
